import { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUnit } from 'effector-react';
import { Control } from '@effector-form';
import {
  Tooltip,
  IconButton,
  Badge,
  Button,
  Menu,
  MenuItem,
  ListItemText,
} from '@mui/material';
import { Add, FilterAltOutlined, KeyboardArrowDown } from '@mui/icons-material';
import { $canCreateTickets } from '@features/common';
import { getActivity } from '@features/tickets/libs';
import { ChangeEmployeeButton } from '@ui/atoms';
import {
  $isFilterOpen,
  changedFilterVisibility,
  $isDetailOpen,
  addClicked,
  $columns,
  $tableData,
  $visibilityColumns,
  closeDetail,
  $isFiltersApply,
  searchForm,
} from '../../models';
import { NEW_OPENED_ID } from '../../models/detail-sdui';
import { setOpenedId } from '../../models/detail-sdui-opened-id';
import { $isReplaceEmployee, setReplaceEmployee } from '../../models/replace-employee';
import { exportTickets } from '../../models/ticket-export';
import { ExportButton } from '../export-button';
import { TicketListExport } from '../ticket-list-export';
import * as Styled from './styled';

export const CustomToolbar: FC = () => {
  const { t } = useTranslation();
  const [
    isFilterOpen,
    isDetailOpen,
    columns,
    tableData,
    visibilityColumns,
    isReplaceEmployee,
    isFiltersApply,
    canCreateTickets,
  ] = useUnit([
    $isFilterOpen,
    $isDetailOpen,
    $columns,
    $tableData,
    $visibilityColumns,
    $isReplaceEmployee,
    $isFiltersApply,
    $canCreateTickets,
  ]);

  const [isOpenTicketListExport, setIsOpenTicketListExport] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const hiddenColumnNames: any = Object.values(visibilityColumns).filter(
    (column) => !column
  );
  const isActive = getActivity({ columns, tableData, hiddenColumnNames });

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const onClickFilter = () => changedFilterVisibility(true);
  const handleCloseTicketListExport = () => {
    setIsOpenTicketListExport(false);
    handleClose();
  };

  const handleClickExport = () => {
    exportTickets({
      limit: 1000,
    });
    handleClose();
  };

  const handleClickExportEmail = () => {
    setIsOpenTicketListExport(true);
    handleClose();
  };

  const changeEmployee = () => {
    closeDetail();
    setReplaceEmployee(true);
    handleClose();
  };

  const handleAdd = () => {
    const newVersion = localStorage.getItem('ticketsNewVersion') === 'new' ? true : false;

    addClicked();
    if (newVersion) setOpenedId(NEW_OPENED_ID);
    handleClose();
  };

  useEffect(
    () => () => {
      setIsOpenTicketListExport(false);
    },
    []
  );

  const open = Boolean(anchorEl);

  const renderResponsive = () => {
    if (isFilterOpen || isDetailOpen || isReplaceEmployee) {
      return (
        <>
          <Button
            variant="outlined"
            disableElevation
            onClick={handleClick}
            endIcon={<KeyboardArrowDown />}
          >
            {t('Label.actions')}
          </Button>
          <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
            <MenuItem onClick={changeEmployee} disableRipple disabled={isReplaceEmployee}>
              {t('SubstituteWorker.popover')}
            </MenuItem>
            <MenuItem onClick={handleClickExport} disableRipple disabled={!isActive}>
              <ListItemText>
                {`${t('UploadToExcel')} (1000 ${t('ofTickets')})`}
              </ListItemText>
            </MenuItem>
            <MenuItem onClick={handleClickExportEmail} disableRipple disabled={!isActive}>
              <ListItemText>
                {`${t('SendByEmail')} (40000 ${t('ofTickets')})`}
              </ListItemText>
            </MenuItem>
            {canCreateTickets && (
              <MenuItem onClick={handleAdd} disableRipple disabled={isDetailOpen}>
                {t('AddARequest')}
              </MenuItem>
            )}
          </Menu>
        </>
      );
    }

    return (
      <>
        <ExportButton
          {...{ columns, tableData, hiddenColumnNames }}
          handleClickExport={handleClickExport}
          handleClickExportEmail={handleClickExportEmail}
        />
        <ChangeEmployeeButton disabled={isReplaceEmployee} onClick={changeEmployee} />
        {canCreateTickets && (
          <Button
            startIcon={<Add />}
            onClick={handleAdd}
            disabled={isDetailOpen}
            color="primary"
            variant="contained"
          >
            {t('AddARequest')}
          </Button>
        )}
      </>
    );
  };

  return (
    <Styled.Wrapper>
      <IconButton disabled={isFilterOpen} onClick={onClickFilter}>
        <Tooltip title={t('Filters')}>
          <Badge variant="dot" color="error" invisible={!isFiltersApply}>
            <FilterAltOutlined />
          </Badge>
        </Tooltip>
      </IconButton>

      <div>
        <Control.Search name="search" form={searchForm} />
      </div>

      <Styled.Greedy />
      {renderResponsive()}
      <TicketListExport
        isOpenTicketListExport={isOpenTicketListExport}
        onCloseTicketListExport={handleCloseTicketListExport}
      />
    </Styled.Wrapper>
  );
};
